/**
 * TaskDashboard Component
 * 
 * Main dashboard showing actionable tasks instead of features.
 * Implements "Task-First, Not Feature-First" UX paradigm.
 * 
 * Features:
 * - Auto-generates tasks based on system state
 * - Shows progress tracking
 * - Prioritizes by urgency (high > medium > low)
 * - Provides clear next actions
 * 
 * @see UX_ACTION_PLAN_ENHANCED_2025-10-13.md
 */

import { useTaskGeneration } from '@/hooks/useTaskGeneration';
import { TaskCard } from './TaskCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/auth-context';

export const TaskDashboard = () => {
  const { user, organization } = useAuth();
  const { tasks, completedCount, totalCount, progress, isLoading, hasActiveTasks } = useTaskGeneration();

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // No tasks (all done!)
  if (!hasActiveTasks && totalCount === 0) {
    return (
      <div className="p-6">
        <Card className="bg-validation-low-bg border-validation-low rounded-2xl">
          <CardHeader>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-circle-check text-3xl text-brand-success"></i>
              <div>
                <CardTitle className="text-brand-primary font-space-grotesk">All Caught Up!</CardTitle>
                <CardDescription className="text-brand-text-secondary">
                  You have no pending tasks. Great work! 🎉
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <Card className="rounded-2xl">
                <CardHeader className="pb-3">
                  <CardDescription className="text-brand-text-secondary">Transactions</CardDescription>
                  <CardTitle className="text-2xl font-space-grotesk text-brand-primary">0</CardTitle>
                </CardHeader>
              </Card>
              <Card className="rounded-2xl">
                <CardHeader className="pb-3">
                  <CardDescription className="text-brand-text-secondary">Pending Reviews</CardDescription>
                  <CardTitle className="text-2xl font-space-grotesk text-brand-primary">0</CardTitle>
                </CardHeader>
              </Card>
              <Card className="rounded-2xl">
                <CardHeader className="pb-3">
                  <CardDescription className="text-brand-text-secondary">Issues</CardDescription>
                  <CardTitle className="text-2xl font-space-grotesk text-brand-primary">0</CardTitle>
                </CardHeader>
              </Card>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold font-space-grotesk text-brand-primary">
          Your Workspace
        </h1>
        <p className="text-brand-text-secondary">
          Welcome back, {user?.email}! Here's what needs your attention.
        </p>
      </div>

      {/* Progress Card */}
      <Card className="rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-space-grotesk text-brand-primary">Today's Progress</CardTitle>
              <CardDescription className="mt-1 text-brand-text-secondary">
                {completedCount} of {totalCount} tasks completed
              </CardDescription>
            </div>
            <div className="text-3xl">
              {progress === 100 ? '🎉' : progress >= 50 ? '💪' : '🚀'}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Progress value={progress} className="h-3" />
            <div className="flex justify-between text-sm text-brand-text-secondary">
              <span>{Math.round(progress)}% complete</span>
              <span>{totalCount - completedCount} remaining</span>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="text-center">
              <div className="text-2xl font-bold font-space-grotesk text-validation-critical">
                {tasks.filter(t => t.priority === 'high' && t.status !== 'completed').length}
              </div>
              <div className="text-xs text-brand-text-secondary mt-1">High Priority</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold font-space-grotesk text-validation-high">
                {tasks.filter(t => t.priority === 'medium' && t.status !== 'completed').length}
              </div>
              <div className="text-xs text-brand-text-secondary mt-1">Medium</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold font-space-grotesk text-validation-low">
                {tasks.filter(t => t.priority === 'low' && t.status !== 'completed').length}
              </div>
              <div className="text-xs text-brand-text-secondary mt-1">Low Priority</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* High Priority Alert */}
      {tasks.some(t => t.priority === 'high' && t.status !== 'completed') && (
        <Alert className="bg-validation-critical-bg border-validation-critical rounded-2xl">
          <i className="fa-solid fa-triangle-exclamation text-validation-critical"></i>
          <AlertDescription className="text-brand-text-primary ml-6">
            You have <strong>{tasks.filter(t => t.priority === 'high' && t.status !== 'completed').length}</strong> high-priority tasks that need immediate attention.
          </AlertDescription>
        </Alert>
      )}

      {/* Tasks List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold font-space-grotesk text-brand-primary">
            Your Tasks
          </h2>
          {completedCount > 0 && (
            <div className="flex items-center gap-2 text-brand-success text-sm font-medium">
              <i className="fa-solid fa-arrow-trend-up"></i>
              <span>Making progress!</span>
            </div>
          )}
        </div>

        {tasks.map((task) => (
          <TaskCard key={task.id} task={task} />
        ))}
      </div>

      {/* Footer Tips */}
      <Card className="bg-brand-accent/20 border-brand-accent rounded-2xl">
        <CardHeader>
          <CardTitle className="text-brand-primary text-sm font-space-grotesk flex items-center gap-2">
            <i className="fa-solid fa-lightbulb"></i>
            Pro Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1 text-sm text-brand-text-primary">
            <li>• Tasks are automatically prioritized by urgency</li>
            <li>• Estimated times help you plan your work</li>
            <li>• Click any task card to jump directly to the action</li>
            <li>• High confidence AI classifications can be approved in bulk</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};
